// Imports for external libraries go here.
import { FC, useEffect, useState } from 'react';

import { LazyQueryHookOptions, useLazyQuery } from '@apollo/client';
import { phoenixLegalConsentsListQuery } from '@marriott/mi-legal-graphql';
import { getApplicableAndSortedConsents, isEmpty } from '../../utils/consents';
import { ConsentsList } from './ConsentsList';
import { ConsentErrorType } from './ConsentsList/ConsentsList.types';
import { LegalConsent, LegalConsentsProps, PhoenixLegalConsentsListType, SectionsList } from './LegalConsents.types';
import { Messages } from '@marriott/mi-ui-library';
import { StyledLegalConsents } from './LegalConsents.styles';
import { LegalConsentsAuthorModeAndApiErrorHandler } from './LegalConsentsAuthorModeAndApiErrorHandler';
import { generateApolloClientHeadersForUXLCalls } from '../../utils/uxlHelper';

const DEFAULT_SECTION_LIST: SectionsList = {
  items: [],
  gCloseAccordionBtnLabel: null,
  gOpenAccordionBtnLabel: null,
  gOpenInAModalSuffix: null,
  gOpenInANewTabSuffix: null,
};

export const LegalConsents: FC<LegalConsentsProps> = ({
  sessionId,
  countryCode,
  isValidateErrors,
  locale,
  customLoader,
  option,
  consentsState,
  isAuthorMode,
  anchorBehavior = 'default',
  linkBehavior = 'default',
  setConsentsState,
  onConsentChange,
  onApiError,
}) => {
  const [consentsResponse, setConsentsResponse] = useState<SectionsList>(DEFAULT_SECTION_LIST);
  const [getLegalConsentsUxl, { loading, error }] =
    useLazyQuery<PhoenixLegalConsentsListType>(phoenixLegalConsentsListQuery);

  // const getLegalConsents = () => {
  //   const uxlOptions: LazyQueryHookOptions = {
  //     // variables: { locale },
  //     context: {
  //       // uri: 'https://marriott-sites2-qa.adobecqms.net/graphql/execute.json/legal-consents/Locale-specific',
  //       // uri: '/graphql/execute.json/legal-consents/getCountryConsents',
  //       headers: {
  //         'x-request-id': `Phoenix-Legal-${sessionId}`,
  //       },
  //     },
  //     fetchPolicy: 'network-only',
  //   };
  //   return getLegalConsentsUxl(uxlOptions);
  // };

  const getLegalConsents = () => {
    const uxlOptions: LazyQueryHookOptions = {
      variables: { locale },
      context: generateApolloClientHeadersForUXLCalls(sessionId, 'phoenixLegalConsentsListQuery', locale),
      fetchPolicy: 'network-only',
    };
    return getLegalConsentsUxl(uxlOptions);
  };

  useEffect(() => {
    const fetchLegalConsents = async () => {
      const legalConsentsRep = await getLegalConsents();
      // const allConsents = mockLegalConsentsProps.data?.sectionsList.items || [];
      const sectionsList =
        legalConsentsRep.data?.content?.legal?.legalConsentsByLocale?.aemData?.data?.sectionsList ||
        DEFAULT_SECTION_LIST;

      // TODO: add validation for null check if request failed

      const {
        items: allConsents,
        gOpenInAModalSuffix,
        gOpenInANewTabSuffix,
        gCloseAccordionBtnLabel,
        gOpenAccordionBtnLabel,
      } = sectionsList;

      const updateConsentResponse = (consents: LegalConsent[]) =>
        setConsentsResponse({
          items: consents,
          gCloseAccordionBtnLabel,
          gOpenAccordionBtnLabel,
          gOpenInAModalSuffix,
          gOpenInANewTabSuffix,
        });

      if (isEmpty(allConsents)) {
        updateConsentResponse(DEFAULT_SECTION_LIST.items);
        setConsentsState({
          sectionTypes: {},
          list: [] as LegalConsent[],
          consentsLedger: {},
          error: { type: ConsentErrorType.NONE, message: null },
        });
        return;
      }

      const sortedConsentListData = getApplicableAndSortedConsents({
        allConsents,
        preStateLedger: consentsState?.consentsLedger,
        countryCode,
        option,
        anchorBehavior,
        ariaModalText: gOpenInAModalSuffix,
        ariaWindowText: gOpenInANewTabSuffix,
      });

      console.log('sortedConsentListData payload ', {
        allConsents,
        preStateLedger: consentsState?.consentsLedger,
        countryCode,
        option,
      });
      console.log('sortedConsentListData ', sortedConsentListData);

      updateConsentResponse(allConsents);
      setConsentsState(sortedConsentListData);
    };
    !isAuthorMode && fetchLegalConsents();
  }, [locale, isAuthorMode]);

  // If option changes then
  // We don't have to call the API again to to fetch the data
  // Pass the same response as previous and filter out applicable consent list with respect to new option value
  useEffect(() => {
    const {
      items: allConsents,
      gOpenInAModalSuffix: ariaModalText,
      gOpenInANewTabSuffix: ariaWindowText,
    } = consentsResponse;
    const sortedConsentListData = getApplicableAndSortedConsents({
      allConsents,
      countryCode,
      option,
      anchorBehavior,
      ariaModalText,
      ariaWindowText,
    });
    setConsentsState(sortedConsentListData);
    console.log('sortedConsentListData payload ', {
      consentsResponse,
      preStateLedger: consentsState?.consentsLedger,
      countryCode,
      option,
    });
    console.log('sortedConsentListData ', sortedConsentListData);
  }, [option, countryCode, consentsResponse]);

  const isValidationErrorFound = consentsState.error.type === ConsentErrorType.INTERNAL_VALIDATION_ERROR;

  if (isAuthorMode || error) {
    return (
      <LegalConsentsAuthorModeAndApiErrorHandler
        isAuthorMode={isAuthorMode}
        isApiError={Boolean(error)}
        onApiError={onApiError}
      />
    );
  }

  return (
    <StyledLegalConsents data-component-name="o-legal-LegalConsents" className="m-legal-consents-container">
      {loading && (customLoader || <div data-testid="skeleton-loader" className="skeleton-loader"></div>)}
      {isValidationErrorFound && (
        <Messages messageType="error-sev1" className="mb-4" messageText={consentsState.error.message!} />
      )}
      {!loading && (
        <ConsentsList
          consentState={consentsState}
          anchorBehavior={anchorBehavior}
          linkBehavior={linkBehavior}
          onConsentChange={onConsentChange}
          validateErrors={isValidateErrors}
          closeAccordionBtnLabel={consentsResponse.gCloseAccordionBtnLabel}
          openAccordionBtnLabel={consentsResponse.gOpenAccordionBtnLabel}
        />
      )}
    </StyledLegalConsents>
  );
};
